/**
 * PrototypeFixCard — shown under the assistant message whose prototype build stopped fixing
 * itself (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4b): the automatic attempts ran out, or the same
 * error came back. Presentation only; the host owns every action.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TriangleAlert } from 'lucide-react';
import { Button } from '../atoms/button';
import { cn } from '../../lib/utils';

export interface PrototypeFixCardProps {
  /** Automatic fix attempts used. */
  fixAttempts: number;
  /** Automatic fix attempts allowed (5, +2 per "Try another fix"). */
  fixAttemptsGranted: number;
  /** False for a first build: there is no earlier version to go back to. */
  canUndo?: boolean;
  /** The action in flight; disables the buttons. */
  busyAction?: 'retry' | 'undo' | 'rebuild' | null;
  /** E.g. the billing notice when there are no credits for another attempt. */
  notice?: string | null;
  /** `timeout`: the turn ran out of its time budget rather than its attempts. */
  reason?: 'timeout' | null;
  onTryAnotherFix?: () => void;
  onUndo?: () => void;
  /**
   * "Build again": run the request once more from the last finished version, instead of
   * another fix on top of the build that keeps failing.
   */
  onBuildAgain?: () => void;
  onTellAi?: () => void;
  /** Resolves the failing build output shown under Details. */
  onLoadDetails?: () => Promise<string | null>;
  isRtl?: boolean;
  className?: string;
  testId?: string;
}

export const PrototypeFixCard: React.FC<PrototypeFixCardProps> = ({
  fixAttempts,
  fixAttemptsGranted,
  canUndo = false,
  busyAction = null,
  notice,
  reason = null,
  onTryAnotherFix,
  onUndo,
  onBuildAgain,
  onTellAi,
  onLoadDetails,
  isRtl,
  className,
  testId = 'prototype-fix-card',
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState<string | null>(null);
  const [detailsState, setDetailsState] = useState<'idle' | 'loading' | 'error'>('idle');
  const busy = busyAction !== null;

  const limitReached = fixAttempts >= fixAttemptsGranted;
  const summary =
    reason === 'timeout'
      ? 'This build ran for 25 minutes without passing, so it was stopped.'
      : limitReached
        ? `Automatic fixes stopped after ${fixAttempts} of ${fixAttemptsGranted} attempts.`
        : 'The same error came back, so automatic fixing stopped.';

  const toggleDetails = (): void => {
    const next = !detailsOpen;
    setDetailsOpen(next);
    if (!next || details !== null || !onLoadDetails || detailsState === 'loading') return;
    setDetailsState('loading');
    onLoadDetails()
      .then((log) => {
        setDetails(log ?? '');
        setDetailsState('idle');
      })
      .catch(() => setDetailsState('error'));
  };

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4',
        'dark:border-amber-900 dark:bg-amber-950/40',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <TriangleAlert size={20} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 text-sm text-gray-800 dark:text-gray-100">
          <p className="font-semibold">This build needs your input</p>
          <p className="mt-1 text-gray-600 dark:text-gray-300">{summary}</p>
          {notice ? (
            <p className="mt-1 text-amber-700 dark:text-amber-300" data-testid={`${testId}-notice`}>
              {notice}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {onTryAnotherFix ? (
          <Button
            variant="primary"
            size="xs"
            onClick={onTryAnotherFix}
            disabled={busy}
            data-testid={`${testId}-retry`}
          >
            {busyAction === 'retry' ? 'Starting…' : 'Try another fix'}
          </Button>
        ) : null}
        {onBuildAgain ? (
          <Button
            variant="secondary-gray"
            size="xs"
            onClick={onBuildAgain}
            disabled={busy}
            data-testid={`${testId}-build-again`}
          >
            {busyAction === 'rebuild' ? 'Starting…' : 'Build again'}
          </Button>
        ) : null}
        {canUndo && onUndo ? (
          <Button
            variant="secondary-gray"
            size="xs"
            onClick={onUndo}
            disabled={busy}
            data-testid={`${testId}-undo`}
          >
            {busyAction === 'undo' ? 'Undoing…' : 'Undo this change'}
          </Button>
        ) : null}
        {onTellAi ? (
          <Button
            variant="secondary-gray"
            size="xs"
            onClick={onTellAi}
            disabled={busy}
            data-testid={`${testId}-tell-ai`}
          >
            Tell the AI what to change
          </Button>
        ) : null}
        {onLoadDetails ? (
          <Button
            variant="tertiary-gray"
            size="xs"
            onClick={toggleDetails}
            aria-expanded={detailsOpen}
            data-testid={`${testId}-details`}
          >
            Details
            {detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        ) : null}
      </div>

      {detailsOpen ? (
        <div className="overflow-x-auto rounded-lg bg-gray-900 p-3" data-testid={`${testId}-log`}>
          <pre dir="ltr" className="whitespace-pre text-xs leading-5 text-gray-100">
            {detailsState === 'loading'
              ? 'Loading the build output…'
              : detailsState === 'error'
                ? "Couldn't load the build output."
                : details || 'No build output was saved for this version.'}
          </pre>
        </div>
      ) : null}
    </div>
  );
};

export default PrototypeFixCard;
